import { z } from "zod";
import { optionalUnlessRequired, type FieldDescriptor } from "./types.js";

/**
 * Paired State → LGA control backed by the seeded 36 states + FCT / 774 LGA dataset
 * (docs/01-PRODUCT-SPEC.md §3.2). "This appears in nearly every one of your forms; it should not
 * be rebuilt as a dropdown with typed options each time."
 *
 * Options are server-backed (`GET /public/lookup/states` and `/lgas`), so the pair's *membership*
 * — that this LGA really belongs to that state — is checked server-side at submit against
 * `ng_states`/`ng_lgas`. This schema enforces the shape and that both halves are answered together.
 */

/**
 * The JV's host states: where Seplat Energy / NNPC operate.
 *
 * This is reference data about the joint venture, and it is **not** the same thing as where a
 * programme runs — the distinction this file used to collapse. Teachers Empowerment and the Pearls
 * Quiz run in Imo, Delta and Edo; Loco Parentis in Delta and Edo; Scholarship is open to every
 * state. Imo is a programme area without being a JV host state, so a single global pair could
 * never express all four, and the one it did express blocked the others: a form restricted to
 * "host states" refuses an Imo teacher outright.
 *
 * Which states a form accepts is therefore `allowedStates` on the field below — a decision taken
 * once, in the builder, and carried in the published version. This constant remains only as the
 * meaning of the legacy `restrictToHostStates` flag, which two live forms were published with.
 */
export const HOST_STATES = ["Delta", "Edo"] as const;

/**
 * The seed stores full names — "Delta State", "Edo State" — while the programme talks about
 * "Delta" and "Edo". Comparing the two literally matched nothing and rendered an empty dropdown on
 * a live form, so both sides are normalised rather than one being assumed to match the other.
 */
const normaliseState = (value: string): string =>
  value
    .trim()
    .replace(/\s+state$/i, "")
    .toLowerCase();

export const isHostState = (value: string): boolean =>
  HOST_STATES.some((host) => normaliseState(host) === normaliseState(value));

const configSchema = z.object({
  stateLabel: z.string().max(100).default("State"),
  lgaLabel: z.string().max(100).default("Local Government Area"),
  /**
   * The states this form accepts. Empty means every state, which is what the Scholarship
   * programme needs and what an unconfigured field should do.
   *
   * Stored as state *names* — matching what `GET /public/lookup/states` returns and what the
   * renderer puts in the option value — and compared normalised, so "Imo" and "Imo State" are the
   * same answer however the list was typed.
   */
  allowedStates: z.array(z.string().trim().min(1).max(60)).max(40).default([]),
  /** Legacy: the JV host pair, Delta and Edo. Superseded by `allowedStates`; see HOST_STATES. */
  restrictToHostStates: z.boolean().default(false),
});

/**
 * The states a field actually accepts, or `null` for "all of them".
 *
 * `allowedStates` wins when set. `restrictToHostStates` is honoured on its own so that the two
 * forms already published with it keep the rules their applicants were shown — a published
 * FormVersion is immutable, and that includes what it refuses.
 */
export const allowedStatesFor = (config: {
  allowedStates?: string[];
  restrictToHostStates?: boolean;
}): readonly string[] | null => {
  if (config.allowedStates && config.allowedStates.length > 0) return config.allowedStates;
  if (config.restrictToHostStates) return HOST_STATES;
  return null;
};

/** Whether an answered state is one of them, comparing "Imo" and "Imo State" as equal. */
export const isStateAllowed = (
  value: string,
  allowed: readonly string[] | null | undefined,
): boolean =>
  !allowed ||
  allowed.length === 0 ||
  allowed.some((state) => normaliseState(state) === normaliseState(value));

/** "Imo, Delta and Edo" — the list as an applicant should read it, not as it is stored. */
const readableList = (states: readonly string[]): string => {
  const names = states.map((state) => state.trim().replace(/\s+state$/i, ""));
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]!}`;
};

export const ngStateLgaField: FieldDescriptor<z.infer<typeof configSchema>> = {
  type: "ng_state_lga",
  label: "Nigerian State and LGA",
  category: "lookup",
  icon: "Map",
  configSchema,
  valueSchema: (config, isRequired) => {
    const allowed = allowedStatesFor(config);
    const base = z
      .object({ state: z.string().trim(), lga: z.string().trim() })
      .strict()
      .superRefine((value, ctx) => {
        // Enforced here, not only in the renderer: the browser's option list is a convenience and
        // a submitted value is never trusted on the strength of what the UI offered.
        if (allowed && value.state && !isStateAllowed(value.state, allowed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["state"],
            message: `This programme is open to ${readableList(allowed)} only`,
          });
        }
        // An LGA without its state is meaningless — several LGA names repeat across states.
        if (value.lga && !value.state) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["state"],
            message: "Select a state first",
          });
        }
        if (isRequired && (!value.state || !value.lga)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: value.state ? ["lga"] : ["state"],
            message: "This field is required",
          });
        }
      });
    return optionalUnlessRequired(base, isRequired);
  },
  defaultConfig: {
    stateLabel: "State",
    lgaLabel: "Local Government Area",
    allowedStates: [],
    restrictToHostStates: false,
  },
  supportsProfileBinding: true,
  profileParts: { state: "address.state", lga: "address.lga" },
  defaultSearchable: true,
  renderHints: { isComposite: true, isServerBacked: true },
};
